<template>
  <div class="portal-registration-container">
    <Loading
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="registration-card">
      <div class="logo-container">
        <VasionLogoWhite class="vasion-wordmark" />
      </div>
      <h1 class="registration-title">Set your password to complete access setup</h1>
      <div class="password-requirements">
        <p class="registration-text">Passwords must contain the following:</p>
        <ul class="registration-text">
          <li>Minimum 12 characters</li>
          <li>Maximum 40 characters</li>
          <li>At least one uppercase letter</li>
          <li>At least one lowercase letter</li>
          <li>At least one number</li>
          <li>At least one special character (!@#$%^&*)</li>
        </ul>
      </div>
      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label class="form-label">EMAIL</label>
          <VasionInput
            id="email"
            v-model="email"
            placeholder="Email"
            :disabled="true"
            inputType="top-black"
            isDisabled
          />
        </div>

        <div class="form-group">
          <label class="form-label">NEW PASSWORD</label>
          <!-- Hidden dummy input to "trick" password managers -->
          <input type="text" style="display: none" name="fakeusernameremembered" />
          <input type="password" style="display: none" name="fakepasswordremembered" />
          <VasionInput
            id="newPassword"
            v-model="newPassword"
            placeholder="New Password"
            specialType="password"
            :canToggleShowPassword="true"
            :isInErrorState="validationError"
            inputType="top-black"
            autocomplete="new-password"
            ref="newPasswordInput"
          />
        </div>

        <div class="form-group">
          <label class="form-label">CONFIRM PASSWORD</label>
          <VasionInput
            id="confirmPassword"
            v-model="confirmPassword"
            placeholder="Confirm Password"
            specialType="password"
            :canToggleShowPassword="true"
            :isInErrorState="validationError"
            inputType="top-black"
            autocomplete="new-password"
            ref="confirmPasswordInput"
          />
          <label v-if="validationError" class="validation-error-message">{{
            validationErrorMessage
          }}</label>
        </div>

        <div class="button-container">
          <VasionButton
            id="register-submit"
            :isFullWidth="true"
            :is-submit="true"
            classProp="primary"
            :isDisabled="isSubmitDisabled"
          >
            <span v-if="!isResettingPassword">Continue</span>
            <div v-else id="loader-box">
              <Loading
                :active.sync="isResettingPassword"
                :is-full-page="false"
                loader="dots"
                :color="'white'"
                :background-color="'#3D2562'"
              />
            </div>
          </VasionButton>
        </div>
      </form>
    </div>

    <VasionSnackbar
      id="portlal-registration-snackbar"
      ref="portlalRegistrationSnackbar"
      :showSnackbarBool.sync="snackbar.showSnackbar"
      :snackbarImage="snackbar.snackbarImage"
      :snackbarSubTitle="snackbar.snackbarSubTitle"
      :snackbarTitle="snackbar.snackbarTitle"
    />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'

export default {
  name: 'PortalUserRegistration',
  components: {
    Loading,
  },
  data() {
    return {
      confirmPassword: '',
      email: '',
      invitationToken: '',
      isLoading: false,
      isResettingPassword: false,
      loaderBackgroundColor,
      loaderColor,
      newPassword: '',
      snackbar: {
        showSnackbar: false,
        snackbarImage: false,
        snackbarSubTitle: '',
        snackbarTitle: '',
      },
      validationError: false,
      validationErrorMessage: '',
    }
  },
  computed: {
    isSignedIn() { return this.$store.state.common.isSignedIn },
    isSubmitDisabled() {
      return !this.newPassword || !this.confirmPassword || this.newPassword !== this.confirmPassword
    },
  },
  async created() {
    await this.$store.dispatch('common/checkIsAuthenticated')
    if (this.isSignedIn) {
      this.$store.dispatch('common/signOut')
    }
    
    // Get the invitation token from the URL
    this.invitationToken = this.$route.params.token

    // Validate the token and get the user's email
    this.validateInvitationToken()
  },
  mounted() {
    this.preventPasswordManagerAutofill()
  },
  methods: {
    async handleSubmit() {
      if (this.isSubmitDisabled) return

      this.isResettingPassword = true
      this.validationError = false

      await this.validatePassword()
      if (this.validationError) {
        this.isResettingPassword = false
        return
      }

      try {
        const responseString = await this.$store.dispatch('common/resetPassword', {
          GUID: this.invitationToken,
          Password: this.newPassword,
        })

        if (responseString) {
          this.showSnackbar(
            'Registration Failed',
            'An error occurred during registration. Please contact your administrator.',
            false
          )
        } else {
          // TODO: REDIRECT TO THE LOGIN PAGE WITH AUTOLOGIN
          this.showSnackbar(
            'Registration Complete',
            'Your password has been set successfully. Redirecting to login...',
            true
          )
          setTimeout(() => {
            this.$router.push({ name: 'TheLogin' })
          }, 2000)
        }
      } catch (error) {
        this.showSnackbar(
          'Registration Failed',
          'An error occurred during registration. Please try again later.',
          false
        )
      } finally {
        this.isResettingPassword = false
      }
    },
    preventPasswordManagerAutofill() {
      const setReadonlyThenRemove = ref => {
        if (ref && ref.$el) {
          const inputEl = ref.$el.querySelector('input')
          if (inputEl) {
            inputEl.setAttribute('readonly', 'readonly')
            setTimeout(() => inputEl.removeAttribute('readonly'), 100)
          }
        }
      }

      setReadonlyThenRemove(this.$refs.newPasswordInput)
      setReadonlyThenRemove(this.$refs.confirmPasswordInput)
    },
    showSnackbar(title, subTitle, isSuccess) {
      this.snackbar = {
        snackbarTitle: title,
        snackbarSubTitle: subTitle,
        snackbarImage: isSuccess,
        showSnackbar: true,
      }
    },
    async validateInvitationToken() {
      try {
        this.isLoading = true

        const isValidToken = await this.$store.dispatch('common/resetPasswordCheckGuid', {
          Value: this.invitationToken,
        })

        if (isValidToken) {
          const username = await this.$store.dispatch(
            'portalUser/getPortalUserUsernameByToken',
            this.invitationToken
          )

          if (username) {
            this.email = username.username
          } else {
            throw new Error('Failed to retrieve username')
          }
        } else {
          this.showSnackbar(
            'Invalid Invitation',
            'The invitation link is invalid or has expired. Please contact your administrator.',
            false
          )

          setTimeout(() => {
            this.$router.push({ name: 'TheLogin' })
          }, 3000)
        }
      } catch (error) {
        this.showSnackbar(
          'Error',
          'An error occurred while validating your invitation. Please try again later.',
          false
        )
      } finally {
        this.isLoading = false
      }
    },
    async validatePassword() {
      // Check if passwords match
      if (this.newPassword !== this.confirmPassword) {
        this.validationError = true
        this.validationErrorMessage = 'Passwords do not match.'
        return
      }

      // Local password validation based on requirements
      const validations = [
        {
          test: this.newPassword.length >= 12,
          message: 'Password should be at least 12 characters long.',
        },
        {
          test: this.newPassword.length <= 40,
          message: 'Password can only be 40 characters long.',
        },
        {
          test: /[A-Z]/.test(this.newPassword),
          message: 'Password must have at least one uppercase letter.',
        },
        {
          test: /[a-z]/.test(this.newPassword),
          message: 'Password must have at least one lowercase letter.',
        },
        {
          test: /[0-9]/.test(this.newPassword),
          message: 'Password must have at least one number.',
        },
        {
          test: /[!@#$%^&*]/.test(this.newPassword),
          message: 'Password must have at least one special character (!@#$%^&*).',
        },
      ]

      // Find the first validation that fails
      const failedValidation = validations.find(validation => !validation.test)
      if (failedValidation) {
        this.validationError = true
        this.validationErrorMessage = failedValidation.message
      } else {
        this.validationError = false
        this.validationErrorMessage = ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';
@import '@/assets/css/vuetify3Breakpoints.scss';

.portal-registration-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: $grey-700;
  background-image: url('~@/assets/images/vasion-logo-texture.png');
  background-size: cover;
  background-repeat: no-repeat;
  padding: 20px;
}

.registration-card {
  width: 100%;
  max-width: 470px;
}

.logo-container {
  text-align: center;
  margin-bottom: 0px;

  @include breakpoint(sm) {
    margin-bottom: 20px;
  }

  @include breakpoint(md) {
    margin-bottom: 30px;
  }

  .vasion-wordmark {
    width: 100%;
    max-width: 257px;
    fill: $orange-300;
  }
}

.registration-title {
  color: $on-primary;
  font-family: $font-family-book;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  text-align: center;
  margin: 0 0 8px;

  @include breakpoint(sm) {
    margin-bottom: 12px;
  }

  @include breakpoint(md) {
    margin-bottom: 16px;
  }
}

.password-requirements {
  max-width: 334px;
  margin: 0 auto;
  margin: 0 auto 16px;
}
.registration-text {
  color: $on-primary;
  font-family: $font-family-book;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  margin-bottom: 0px;
}

form {
  max-width: 380px;
  margin: 0 auto;
}

.form-group {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: $grey-300;
  margin-bottom: 4px;
}

.validation-error-message {
  display: block;
  color: $error;
  font-size: 12px;
  margin-top: 8px;
}

.button-container {
  margin-top: 32px;
}

::v-deep #register-submit button {
  margin-left: 0;
  margin-right: 0;
  width: 100%;
}

#loader-box {
  min-height: 36px;
  min-width: 419px;
  z-index: 30;
}
</style>
